import React, { useRef } from 'react';

/**
 * Reusable Table / List Pagination Component
 * Caps items at pageSize (default 100).
 * Displays current range, total count, Prev/Next buttons and page numbers with ellipses.
 * Smoothly scrolls to the top of the table on page changes to prevent viewport jumping.
 */
const TablePagination = ({ 
    currentPage = 1, 
    totalItems = 0, 
    pageSize = 100, 
    onPageChange,
    showMaxLabel = true,
    position = 'bottom',
    hideIfSinglePage = false
}) => {
    const containerRef = useRef(null);
    const totalPages = Math.ceil(totalItems / pageSize);
    if (totalItems === 0) return null;
    if (hideIfSinglePage && totalPages <= 1) return null;

    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    const isTop = position === 'top';
    const borderStyle = isTop ? 'mb-3 pb-2 pt-1' : 'mt-2 pt-2';

    const handlePageClick = (p, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (p === currentPage || p < 1 || p > totalPages) return;

        // Capture parent card/container before state update triggers re-render
        const parentCard = containerRef.current?.closest('.card') || containerRef.current?.parentElement;

        onPageChange(p);

        // Only scroll to top if clicking from the bottom pagination bar
        if (!isTop && parentCard) {
            requestAnimationFrame(() => {
                const rect = parentCard.getBoundingClientRect();
                const navOffset = 90;
                // If top of table is above viewport top (user scrolled down past it)
                if (rect.top < navOffset) {
                    const targetScrollY = rect.top + window.pageYOffset - navOffset;
                    window.scrollTo({
                        top: Math.max(0, targetScrollY),
                        behavior: 'smooth'
                    });
                }
            });
        }
    };

    if (totalPages <= 1) {
        return (
            <div ref={containerRef} className={`flex items-center justify-between px-4 py-2.5 ${borderStyle} text-xs text-neutral-400`}>
                <span>Showing <strong className="text-white font-medium">{startItem}–{endItem}</strong> of <strong className="text-white font-medium">{totalItems}</strong></span>
            </div>
        );
    }

    const getPageNumbers = () => {
        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);
            for (let i = start; i <= end; i++) pages.push(i);
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    return (
        <div ref={containerRef} className={`flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-2.5 ${borderStyle} text-xs text-neutral-400`}>
            <div>
                Showing <strong className="text-white font-medium">{startItem}–{endItem}</strong> of <strong className="text-white font-medium">{totalItems}</strong> items
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
                <button
                    type="button"
                    onClick={(e) => handlePageClick(currentPage - 1, e)}
                    disabled={currentPage <= 1}
                    className="px-2.5 py-1 rounded bg-dark-200 hover:bg-dark-300 disabled:opacity-40 disabled:cursor-not-allowed text-neutral-300 border border-dark-300 transition-colors text-xs font-medium cursor-pointer"
                >
                    ← Prev
                </button>
                {getPageNumbers().map((p, idx) => (
                    p === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-2 py-1 text-neutral-500 font-mono">...</span>
                    ) : (
                        <button
                            type="button"
                            key={p}
                            onClick={(e) => handlePageClick(p, e)}
                            className={`min-w-[28px] h-7 px-2 rounded text-xs font-mono font-medium transition-colors border cursor-pointer ${
                                currentPage === p 
                                    ? 'bg-primary-600 text-white border-primary-500 shadow-sm' 
                                    : 'bg-dark-200 text-neutral-300 hover:bg-dark-300 border-dark-300'
                            }`}
                        >
                            {p}
                        </button>
                    )
                ))}
                <button
                    type="button"
                    onClick={(e) => handlePageClick(currentPage + 1, e)}
                    disabled={currentPage >= totalPages}
                    className="px-2.5 py-1 rounded bg-dark-200 hover:bg-dark-300 disabled:opacity-40 disabled:cursor-not-allowed text-neutral-300 border border-dark-300 transition-colors text-xs font-medium cursor-pointer"
                >
                    Next →
                </button>
            </div>
        </div>
    );
};

export default TablePagination;
